/**
 * Le module Comptabilité — le calcul, en fonctions pures.
 *
 * Cadrage « Stafa v2 — Étape 10 : la facturation et le recouvrement », clos le
 * 09/09/2026. Maquette `docs/maquettes/facturation.html`.
 *
 * Même découpe que partout : les écrans dessinent, ce fichier calcule, et rien
 * ici ne connaît React ni le réseau. Tout se teste sans navigateur.
 *
 *
 * TOUT SE COMPTE EN CENTIMES, ET C'EST LA DÉCISION STRUCTURANTE
 * ---------------------------------------------------------------------------
 * `0.1 + 0.2` vaut `0.30000000000000004` en flottant. Sur une facture, cette
 * poussière finit par un centime d'écart entre le pied et la somme des lignes —
 * et un écart d'un centime sur une pièce comptable est un écart, pas un détail.
 *
 * Donc : on entre en centimes dès la première multiplication, on n'en sort
 * qu'à l'affichage. Les fonctions rendent des ENTIERS.
 *
 *
 * LA REMISE GLOBALE SE VENTILE, ET C'EST LA RÈGLE QUI DICTE LA STRUCTURE
 * ---------------------------------------------------------------------------
 * Une remise de pied sur une facture qui porte du 20 % et du 0 % en
 * autoliquidation NE PEUT PAS se soustraire du total : il faut la répartir au
 * prorata de chaque taux, sinon la TVA déclarée est fausse. C'est pour cette
 * seule raison que `ventilation` rend une liste de parts et non trois nombres.
 *
 * LE PRORATA NE TOMBE PAS JUSTE. Répartir 62,00 € sur deux bases donne des
 * centimes qui ne se recollent pas. La règle retenue : on arrondit chaque part
 * au centime, puis **le reliquat va à la part la plus grosse**. C'est le choix
 * le plus défendable devant un contrôle — l'écart est absorbé là où il pèse le
 * moins en proportion — et il est TESTÉ : la somme des bases ventilées vaut
 * toujours exactement le total après remise, à l'unité près de zéro.
 *
 *
 * CE QUE CE MODULE NE SAIT PAS ENCORE FAIRE
 * ---------------------------------------------------------------------------
 * **L'état « Payée » n'existe pas ici.** La maquette le montre, mais aucune
 * colonne de la base ne dit qu'une facture est encaissée : le règlement est le
 * sous-menu Recouvrement, qui n'est pas construit. Rendre « Payée » aujourd'hui
 * demanderait d'inventer la donnée. `etatFacture` rend donc quatre états, et
 * l'écran n'en montre pas un cinquième. Le jour où Recouvrement pose la
 * colonne, c'est ici que la branche s'ajoute — à un seul endroit.
 */

/** Ce que le pied d'une facture doit à un taux de TVA donné. */
export interface PartTva {
  /** Le taux en pourcentage : 20, 10, 0. */
  readonly taux: number;
  /** La base imposable APRÈS ventilation de la remise globale, en centimes. */
  readonly base: number;
  /** La TVA due sur cette base, en centimes. */
  readonly tva: number;
}

/** Le pied complet d'une facture. Tous les montants sont des centimes. */
export interface Ventilation {
  /** La somme des lignes, remises de ligne déduites, avant remise globale. */
  readonly totalHt: number;
  /** Ce que la remise globale retire. Positif quand elle retire. */
  readonly remise: number;
  /** Le HT sur lequel la TVA se calcule. */
  readonly htApresRemise: number;
  /** Une part par taux présent, du taux le plus fort au plus faible. */
  readonly parts: readonly PartTva[];
  /** La somme des TVA. */
  readonly tva: number;
  /** `htApresRemise + tva`. */
  readonly ttc: number;
}

/** Une ligne telle que l'API la rend : des numériques en texte. */
export interface LigneCalculable {
  readonly quantite: string | number;
  readonly prix_unitaire: string | number;
  readonly remise?: string | number | null;
  readonly taux_tva: string | number;
}

/**
 * L'ARRONDI AU CENTIME, symétrique autour de zéro.
 *
 * `Math.round(-0.5)` vaut `-0` en JavaScript : il arrondit vers le haut, donc
 * vers zéro pour les négatifs. Un avoir porte des montants négatifs ; sans
 * cette précaution, un avoir de −0,005 € s'arrondirait à 0 et une facture de
 * +0,005 € à 0,01 €. On arrondit la valeur absolue, on remet le signe.
 */
export function arrondi(valeur: number): number {
  return valeur < 0 ? -Math.round(-valeur) : Math.round(valeur);
}

/**
 * UN NOMBRE, D'OÙ QU'IL VIENNE. `null`, `''` et le non-numérique valent zéro.
 *
 * postgres.js rend les `numeric` en TEXTE pour ne pas perdre de précision ;
 * les saisies d'écran arrivent en texte aussi, parfois avec une virgule. Les
 * trois chemins se rejoignent ici.
 */
export function nombre(valeur: string | number | null | undefined): number {
  if (typeof valeur === 'number') return Number.isFinite(valeur) ? valeur : 0;
  if (valeur === null || valeur === undefined) return 0;
  const propre = valeur.trim().replace(/\s/gu, '').replace(',', '.');
  if (propre === '') return 0;
  const lu = Number(propre);
  return Number.isFinite(lu) ? lu : 0;
}

/**
 * LE TOTAL D'UNE LIGNE, en centimes : quantité × prix unitaire, remise déduite.
 *
 * L'arrondi tombe UNE FOIS, à la fin. Arrondir le prix remisé puis multiplier
 * par la quantité donnerait un autre chiffre — et c'est le total de ligne qui
 * est affiché, donc c'est lui qui doit être juste.
 */
export function totalLigne(ligne: LigneCalculable): number {
  const quantite = nombre(ligne.quantite);
  const prix = nombre(ligne.prix_unitaire);
  const remise = nombre(ligne.remise ?? 0);
  return arrondi(quantite * prix * 100 * (1 - remise / 100));
}

/**
 * LE PIED DE LA FACTURE : total, remise, bases par taux, TVA, TTC.
 *
 * `remiseGlobale` est un POURCENTAGE, pas un montant — c'est ce que l'écran
 * saisit. Une facture sans ligne rend des zéros et aucune part : le pied ne
 * doit rien affirmer sur une facture vide.
 */
export function ventilation(
  lignes: readonly LigneCalculable[],
  remiseGlobale: string | number | null = null,
): Ventilation {
  const totalHt = lignes.reduce((somme, l) => somme + totalLigne(l), 0);

  const pourcentage = Math.min(Math.max(nombre(remiseGlobale), 0), 100);
  const remise = arrondi((totalHt * pourcentage) / 100);
  const htApresRemise = totalHt - remise;

  if (lignes.length === 0) {
    return { totalHt: 0, remise: 0, htApresRemise: 0, parts: [], tva: 0, ttc: 0 };
  }

  // Une base brute par taux présent, dans l'ordre décroissant du taux.
  const brutes = new Map<number, number>();
  for (const ligne of lignes) {
    const taux = nombre(ligne.taux_tva);
    brutes.set(taux, (brutes.get(taux) ?? 0) + totalLigne(ligne));
  }
  const taux = [...brutes.keys()].sort((a, b) => b - a);

  // LA VENTILATION. Chaque base prend sa part de la remise au prorata, puis le
  // reliquat d'arrondi va à la part la plus grosse EN VALEUR ABSOLUE — sur un
  // avoir, la plus grosse est la plus négative.
  const bases = taux.map((t) => {
    const brute = brutes.get(t) ?? 0;
    if (totalHt === 0) return brute;
    return brute - arrondi((remise * brute) / totalHt);
  });
  const ecart = htApresRemise - bases.reduce((s, b) => s + b, 0);
  if (ecart !== 0) {
    let dominante = 0;
    for (let i = 1; i < bases.length; i += 1) {
      if (Math.abs(bases[i] ?? 0) > Math.abs(bases[dominante] ?? 0)) dominante = i;
    }
    bases[dominante] = (bases[dominante] ?? 0) + ecart;
  }

  const parts = taux.map((t, i) => {
    const base = bases[i] ?? 0;
    return { taux: t, base, tva: arrondi((base * t) / 100) };
  });
  const tva = parts.reduce((somme, p) => somme + p.tva, 0);

  return { totalHt, remise, htApresRemise, parts, tva, ttc: htApresRemise + tva };
}

/**
 * LES GROUPES DE L'ÉDITEUR — et du document, qui est le même découpage.
 *
 * TROIS CATÉGORIES, DEUX GROUPES. C'est la décision du 10/09/2026 : la
 * surcharge carburant s'imprime DANS « Description : » et entre dans le
 * sous-total transport, parce qu'elle est assise sur le prix du transport et
 * en suit le sort — un avoir sur le transport doit emporter sa surcharge.
 *
 * L'ORDRE À L'INTÉRIEUR D'UN GROUPE EST CELUI DU `rang`, jamais celui de la
 * catégorie : deux lignes de transport ne doivent pas changer de place parce
 * qu'une surcharge est apparue entre elles.
 *
 * UNE CATÉGORIE INCONNUE TOMBE DANS « Description : » et n'est PAS écartée.
 * Une ligne qu'on n'affiche pas est une ligne qu'on facture sans la voir ; le
 * total du pied, lui, la compterait. Mieux vaut la montrer au mauvais endroit
 * que la cacher au bon.
 */
export interface GroupeLignes<L extends LigneGroupable = LigneGroupable> {
  /** `description` ou `annexes` — les deux intitulés du document. */
  readonly clef: 'description' | 'annexes';
  /** L'intitulé du groupe, tel que le document l'imprime. */
  readonly titre: string;
  /**
   * LE LIBELLÉ DU SOUS-TOTAL, ÉCRIT À PART et non déduit du titre.
   *
   * Le document dit « Description : » en tête de groupe mais « Sous-total
   * TRANSPORT » en bas — ce ne sont pas les mêmes mots. Déduire l'un de
   * l'autre donnait « Sous-total description », constaté au rendu.
   */
  readonly sousTotalDit: string;
  readonly lignes: readonly L[];
  /** La somme des lignes du groupe, en centimes. */
  readonly total: number;
}

/** Ce qu'il faut d'une ligne pour la grouper. */
export interface LigneGroupable extends LigneCalculable {
  readonly rang: number;
  readonly categorie: string;
}

// GÉNÉRIQUE, et c'est nécessaire : l'écran passe des `LigneFacture` et doit
// les récupérer entières — un groupe de `LigneGroupable` lui ferait perdre
// `id`, `designation` et `ot_numero` en chemin.
export function grouperLignes<L extends LigneGroupable>(
  lignes: readonly L[],
): readonly GroupeLignes<L>[] {
  const dans = (ligne: L): 'description' | 'annexes' =>
    ligne.categorie === 'annexe' ? 'annexes' : 'description';

  const parClef = new Map<'description' | 'annexes', L[]>([
    ['description', []],
    ['annexes', []],
  ]);
  for (const ligne of lignes) parClef.get(dans(ligne))?.push(ligne);

  const titres = { description: 'Description', annexes: 'Prestations annexes' };
  const sousTotaux = {
    description: 'Sous-total transport',
    annexes: 'Sous-total prestations annexes',
  };
  return (['description', 'annexes'] as const).map((clef) => {
    const retenues = [...(parClef.get(clef) ?? [])].sort((a, b) => a.rang - b.rang);
    return {
      clef,
      titre: titres[clef],
      sousTotalDit: sousTotaux[clef],
      lignes: retenues,
      total: retenues.reduce((somme, l) => somme + totalLigne(l), 0),
    };
  });
}

/**
 * Y A-T-IL DÉJÀ UNE SURCHARGE ? Le bouton s'éteint quand oui.
 *
 * Mohamed, le 10/09 : « une seul ligne ». L'écran l'empêche d'abord, un index
 * unique partiel l'empêche vraiment.
 */
export function porteUneSurcharge(lignes: readonly LigneGroupable[]): boolean {
  return lignes.some((l) => l.categorie === 'surcharge');
}

/** Le libellé figé de la surcharge — décision du 10/09, il ne se saisit pas. */
export const LIBELLE_SURCHARGE = 'SURCHARGE CARBURANT';

/**
 * DES CENTIMES EN EUROS LISIBLES : `124000` → « 1 240,00 ».
 *
 * L'espace est une ESPACE FINE INSÉCABLE (U+202F), celle que `Intl` pose en
 * français. Une espace ordinaire couperait « 1 240,00 » en fin de ligne.
 */
export function montant(centimes: number): string {
  return (centimes / 100).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * UN NUMÉRIQUE DE L'API, ÉCRIT POUR LA SAISIE : `'1150.00'` → « 1 150,00 ».
 *
 * postgres.js rend les `numeric` en texte anglo-saxon — « 1150.00 ». Le poser
 * tel quel dans un champ de facture donne à lire des chiffres qu'aucun
 * comptable français n'écrit ainsi, et la virgule qu'il tapera ensuite doit de
 * toute façon être acceptée. Autant afficher d'emblée ce qu'on accepte.
 *
 * `fixe` à faux enlève les décimales inutiles : une quantité de 1 s'écrit « 1 »
 * et non « 1,00 ». Un PRIX garde ses deux décimales, toujours.
 */
export function saisieDite(valeur: string | number | null, fixe = true): string {
  if (valeur === null || valeur === '') return '';
  const n = nombre(valeur);
  return n.toLocaleString('fr-FR', {
    minimumFractionDigits: fixe ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

/**
 * UNE SAISIE FRANÇAISE RENDUE AU FORMAT QUE L'API ACCEPTE :
 * « 1 150,00 » → « 1150.00 ».
 *
 * L'API refuse les séparateurs de milliers — `champs.ts`, fonction `montant` :
 * elle n'échange que la virgule contre un point, puis exige des chiffres. Sans
 * ce nettoyage, un champ qu'on affiche formaté serait refusé dès qu'on le
 * réenregistre sans y toucher.
 *
 * Rend une chaîne VIDE sur une saisie vide, pour que l'appelant décide s'il
 * envoie `null` ou s'il refuse.
 */
export function saisieBrute(texte: string): string {
  const propre = texte.replace(/[\s\u00A0\u202F]/gu, '').replace(',', '.');
  return propre === '' ? '' : propre;
}

/** Un taux tel qu'il se dit dans le pied : « TVA 20 % », « TVA 0 % ». */
export function tauxDit(taux: number): string {
  const propre = Number.isInteger(taux) ? String(taux) : String(taux).replace('.', ',');
  return `TVA ${propre} %`;
}

// ===========================================================================
// L'ÉTAT D'UNE PIÈCE — calculé, jamais saisi
// ===========================================================================

/**
 * Les quatre états qu'on sait déduire aujourd'hui.
 *
 * `payee` manque, et c'est assumé : voir l'en-tête de ce fichier.
 */
export type EtatFacture =
  | 'brouillon'
  | 'emise'
  | 'a_venir'
  | 'en_retard'
  /**
   * PAYÉE — ajouté le 10/09/2026, sur un défaut signalé par l'utilisateur :
   * « une facture marquée comme payée affiche encore l'état en retard ».
   *
   * Il avait raison, et l'en-tête de `20260909200000_factures.sql` le disait
   * déjà : « `payee` n'existe pas encore : elle demande la table des règlements
   * et le lettrage ». Ils existent depuis le module Recouvrement ; l'état
   * manquait donc, et une facture soldée continuait de s'afficher en retard.
   */
  | 'payee';

/** Ce que l'état demande à savoir de la pièce. */
export interface FactureEtatisable {
  readonly type?: string;
  readonly emise_le?: string | null;
  readonly echeance?: string | null;
  /**
   * LA SOMME DES RÈGLEMENTS AFFECTÉS, telle que l'API la rend.
   *
   * `undefined` quand l'appelant ne la connaît pas — un écran qui n'a pas lu le
   * recouvrement. Dans ce cas l'état se calcule comme avant, sans « payée » :
   * mieux vaut ne pas savoir que prétendre qu'une facture n'est pas soldée.
   */
  readonly regle?: string | number | null;
  /**
   * L'ÉTAT CHEZ PENNYLANE — lot 153, 03/11/2026. `true` ou `false` quand la
   * copie des factures Pennylane connaît la pièce ; `null` ou absent sinon.
   * Quand il est connu, IL FAIT FOI et les règlements saisis à la main ne
   * comptent plus (décision de Mohamed : « le recouvrement doit lire les
   * encaissements sur Pennylane »).
   */
  readonly pennylane_payee?: boolean | null;
  /** Les bases par taux : il faut le TTC pour savoir si le règlement le couvre. */
  readonly bases?: readonly BaseTaux[];
  readonly remise_globale?: string | null;
}

/**
 * L'ÉTAT SE DÉDUIT DE TROIS CHOSES, dans cet ordre :
 *
 *   - pas émise            → brouillon ;
 *   - émise sans échéance  → émise (un avoir n'a pas d'échéance) ;
 *   - échéance à venir     → à venir ;
 *   - échéance dépassée    → en retard.
 *
 * `aujourdhui` est un paramètre et non `new Date()` caché dedans : sans ça, la
 * fonction change de résultat selon le jour où le test tourne.
 */
export function etatFacture(
  facture: FactureEtatisable,
  aujourdhui: string,
): EtatFacture {
  if (facture.emise_le === null || facture.emise_le === undefined) return 'brouillon';

  /**
   * PAYÉE PASSE AVANT L'ÉCHÉANCE. Une facture soldée n'est plus en retard,
   * même si son échéance est dépassée depuis trois semaines — c'est le défaut
   * signalé le 10/09/2026.
   *
   * Le règlement se compare au TTC, pas au HT : c'est ce que le client doit.
   * Sur un avoir, les deux sont négatifs et la comparaison tient sans cas
   * particulier. Tout en CENTIMES ENTIERS : `2856.00 - 2856` en flottant ne
   * vaut pas toujours zéro.
   */
  /**
   * PENNYLANE FAIT FOI — lot 153. Une pièce qu'il connaît est payée s'il le
   * dit, et ne l'est pas sinon : les règlements saisis dans Stafa ne sont
   * alors plus regardés.
   */
  if (facture.pennylane_payee === true) return 'payee';
  if (
    facture.pennylane_payee !== false &&
    facture.regle !== undefined &&
    facture.regle !== null &&
    facture.bases !== undefined
  ) {
    const ttc = ventilation(
      facture.bases.map((b) => ({
        quantite: 1,
        prix_unitaire: b.base,
        remise: null,
        taux_tva: b.taux,
      })),
      facture.remise_globale ?? null,
    ).ttc;
    /**
     * LE MÊME DÉFAUT QUE `porteAuCompte`, ET LA MÊME CORRECTION — 17/09/2026.
     *
     * On écrivait ici `type === 'avoir' ? -ttc : ttc`. Les lignes d'un avoir
     * portent DÉJÀ un prix unitaire négatif depuis le 11/09 : la retourner une
     * seconde fois la rendait positive, et un avoir ne passait JAMAIS
     * « Consommé » — son porté positif ne pouvait pas égaler un règlement
     * négatif.
     *
     * LE SIGNE EST DANS LA DONNÉE. Deux endroits tenaient la même vérité ; le
     * jour où l'un a changé, l'autre est resté en arrière.
     */
    const porte = ttc;
    const regle = Math.round(nombre(facture.regle) * 100);
    if (porte !== 0 && porte - regle === 0) return 'payee';
  }

  const echeance = facture.echeance;
  if (echeance === null || echeance === undefined || echeance === '') return 'emise';
  return echeance < aujourdhui ? 'en_retard' : 'a_venir';
}

/** Le mot de l'état, accordé au type de la pièce. */
export function etatDit(etat: EtatFacture, type = 'facture'): string {
  const avoir = type === 'avoir';
  if (etat === 'brouillon') return 'Brouillon';
  // UN AVOIR N'EST PAS « payé », il est CONSOMMÉ : le client ne l'a pas réglé,
  // il l'a déduit de ce qu'il devait.
  if (etat === 'payee') return avoir ? 'Consommé' : 'Payée';
  if (etat === 'emise') return avoir ? 'Émis' : 'Émise';
  if (etat === 'a_venir') return 'À venir';
  return 'En retard';
}

/**
 * DE COMBIEN DE JOURS ON EST EN RETARD. `null` si on ne l'est pas.
 *
 * Le calcul passe par `Date.UTC` : deux dates lues en heure locale se croisent
 * mal quand un changement d'heure tombe entre elles, et rendraient 4,96 jours.
 */
export function joursDeRetard(echeance: string, aujourdhui: string): number | null {
  const jours = (iso: string): number | null => {
    const bouts = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(iso);
    if (bouts === null) return null;
    return (
      Date.UTC(Number(bouts[1]), Number(bouts[2]) - 1, Number(bouts[3])) / 86_400_000
    );
  };
  const fin = jours(echeance);
  const jour = jours(aujourdhui);
  if (fin === null || jour === null) return null;
  return jour > fin ? jour - fin : null;
}

/** Les quatre canaux d'envoi, et ce qu'ils se disent. */
export const CANAUX: readonly { readonly cle: string; readonly nom: string }[] = [
  { cle: 'mail', nom: 'Mail' },
  { cle: 'courrier', nom: 'Courrier' },
  { cle: 'electronique', nom: 'Électronique' },
  { cle: 'automatique', nom: 'Automatique' },
];

/** Le nom d'un canal, ou la clé telle quelle si elle est inconnue. */
export function canalDit(canal: string | null): string {
  if (canal === null) return '';
  return CANAUX.find((c) => c.cle === canal)?.nom ?? canal;
}

// ===========================================================================
// L'ÉCRAN « À FACTURER » — le regroupement et le décompte
// ===========================================================================

/** Un ordre en attente de facture, tel que `GET /factures/a-facturer` le rend. */
export interface OrdreAFacturer {
  readonly id: string;
  readonly numero: string;
  readonly client_id: string;
  readonly client: string;
  readonly frequence_facturation: string | null;
  readonly prix_vente: string | null;
  readonly reference_client: string | null;
  readonly ville_depart: string | null;
  readonly ville_arrivee: string | null;
  readonly jour_fin: string | null;
  readonly envoye_facturation_le: string | null;
  readonly envoye_facturation_par: string | null;
}

/** Un client et ses ordres, tels que le tableau les empile. */
export interface GroupeClient {
  readonly clientId: string;
  readonly nom: string;
  /** `null` vaut `par_envoi` — décision du 09/09, voir la migration. */
  readonly frequence: string;
  readonly ordres: readonly OrdreAFacturer[];
  /** La somme des ventes HT du groupe, en centimes. */
  readonly total: number;
  /** Le 1er du mois prochain, pour un client réglé au mois. `null` sinon. */
  readonly facturableLe: string | null;
}

/**
 * La fréquence effective : `null` se lit `par_envoi`, et rien d'autre.
 *
 * La liste est écrite ici EN TOUTES LETTRES et non importée de `annuaire.ts` :
 * ce que cette fonction garde, c'est qu'une clé inconnue — une fiche ancienne,
 * une frappe — retombe sur `par_envoi` au lieu de faire naître un groupe que
 * personne ne sait facturer. Une liste partagée avec le déroulant ferait
 * silencieusement passer toute clé future.
 *
 * `par_semaine` et `par_quinzaine` s'ajoutent le 10/09/2026.
 *
 * `par_quinzaine` A REÇU SA DATE le 10/09/2026 au soir : « c'est 2 semaines
 * 2 semaines la quinzaine, 15 et 31 ». Voir `prochaineQuinzaine`.
 *
 * `par_semaine` A REÇU LA SIENNE le 11/09/2026 : « le vendredi soir pour la
 * semaine ». Voir `prochainVendredi`.
 *
 * LES QUATRE FRÉQUENCES QUI ATTENDENT UNE DATE SONT DONC SERVIES, et la
 * cinquième — `par_envoi` — n'en veut pas : elle est facturable dès l'envoi,
 * c'est sa définition.
 */
export function frequenceEffective(valeur: string | null): string {
  return valeur === 'par_ordre' ||
    valeur === 'par_semaine' ||
    valeur === 'par_quinzaine' ||
    valeur === 'par_mois'
    ? valeur
    : 'par_envoi';
}

/**
 * LE 1ER DU MOIS PROCHAIN, en ISO. C'est la date que porte la mention
 * « Facturable le 01/10 » d'un client réglé au mois.
 */
export function premierDuMoisProchain(aujourdhui: string): string | null {
  const bouts = /^(\d{4})-(\d{2})-\d{2}$/u.exec(aujourdhui);
  if (bouts === null) return null;
  const annee = Number(bouts[1]);
  const mois = Number(bouts[2]);
  return mois === 12
    ? `${String(annee + 1)}-01-01`
    : `${String(annee)}-${String(mois + 1).padStart(2, '0')}-01`;
}

/**
 * LE DERNIER JOUR DU MOIS, en ISO.
 *
 * Écrit à part parce que « le 31 » n'existe pas tous les mois : février en a
 * 28 ou 29, et quatre mois en ont 30. Poser `-31` en dur fabriquerait une date
 * impossible que le navigateur décalerait en silence au 1er ou au 3 du mois
 * suivant — une facture datée du 3 mars pour la quinzaine de février.
 *
 * `Date.UTC(annee, mois, 0)` rend le dernier jour du mois précédent : on passe
 * donc le mois SUIVANT, en base 0, ce qui revient au mois courant en base 1.
 * L'UTC est délibéré : en heure locale, minuit d'un jour d'été peut retomber
 * sur la veille.
 */
export function dernierJourDuMois(annee: number, mois: number): string {
  const jour = new Date(Date.UTC(annee, mois, 0)).getUTCDate();
  return `${String(annee)}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
}

/**
 * LA PROCHAINE ÉCHÉANCE D'UN CLIENT RÉGLÉ À LA QUINZAINE.
 *
 * Règle donnée le 10/09/2026 : « c'est 2 semaines 2 semaines la quinzaine, 15
 * et 31 ». Deux coupes par mois, les mêmes pour tout le monde : le 15, puis le
 * dernier jour du mois. Aucune colonne d'ancrage par client n'a donc été
 * ajoutée — le jour où un client demandera « moi c'est le 10 et le 25 », il en
 * faudra une, et cette fonction ne saura pas la porter.
 *
 * RIEN LE JOUR DE LA COUPE. Le 15, le client est facturable AUJOURD'HUI : on
 * rend `null`, donc pas de mention « Facturable le… » et pas de rangement en
 * fin de liste. Écrire « Facturable le 15/09 » un 15/09 dirait d'attendre le
 * jour même où il faut facturer.
 *
 * `null` aussi sur une date illisible, comme `premierDuMoisProchain` : une
 * date qu'on ne sait pas lire ne doit pas devenir une date inventée.
 */
export function prochaineQuinzaine(aujourdhui: string): string | null {
  const bouts = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(aujourdhui);
  if (bouts === null) return null;
  const annee = Number(bouts[1]);
  const mois = Number(bouts[2]);
  const jour = Number(bouts[3]);

  if (jour < 15) return `${String(annee)}-${String(mois).padStart(2, '0')}-15`;

  const fin = dernierJourDuMois(annee, mois);
  if (jour === 15 || aujourdhui === fin) return null;
  if (aujourdhui < fin) return fin;

  // Passé le dernier jour du mois — cas qui ne devrait pas exister, mais une
  // date d'entrée fausse ne doit pas rendre une échéance déjà écoulée.
  return mois === 12
    ? `${String(annee + 1)}-01-15`
    : `${String(annee)}-${String(mois + 1).padStart(2, '0')}-15`;
}

/**
 * LA PROCHAINE ÉCHÉANCE D'UN CLIENT RÉGLÉ À LA SEMAINE.
 *
 * Règle donnée le 11/09/2026 : « le vendredi soir pour la semaine ». Le
 * vendredi est donc la coupe ; « le soir » ne se stocke pas — une facture
 * porte une date, pas une heure, et l'écran ne dit que le jour.
 *
 * MÊME RÈGLE QUE LA QUINZAINE le jour de la coupe : le vendredi, on rend
 * `null`. Le client est facturable AUJOURD'HUI ; lui afficher « Facturable le
 * vendredi » un vendredi dirait d'attendre le jour même où il faut facturer.
 *
 * TOUT EN UTC, comme `dernierJourDuMois` : en heure locale, minuit d'un jour
 * d'été retombe sur la veille et le jour de la semaine glisse d'un cran.
 */
export function prochainVendredi(aujourdhui: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(aujourdhui)) return null;
  const jour = new Date(`${aujourdhui}T00:00:00Z`);
  if (Number.isNaN(jour.getTime())) return null;

  // 0 = dimanche … 5 = vendredi, 6 = samedi.
  const combien = (5 - jour.getUTCDay() + 7) % 7;
  if (combien === 0) return null;

  jour.setUTCDate(jour.getUTCDate() + combien);
  return jour.toISOString().slice(0, 10);
}

/** Une date ISO en « 01/10 ». `null` reste `null`. */
export function jourDit(iso: string | null): string | null {
  const bouts = /^\d{4}-(\d{2})-(\d{2})$/u.exec(iso ?? '');
  return bouts === null ? null : `${String(bouts[2])}/${String(bouts[1])}`;
}

/** Une date ISO en « 01/10/2026 ». `null` reste `null`. */
export function dateDite(iso: string | null): string | null {
  const bouts = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(iso ?? '');
  return bouts === null
    ? null
    : `${String(bouts[3])}/${String(bouts[2])}/${String(bouts[1])}`;
}

/**
 * LES ORDRES, GROUPÉS PAR CLIENT, dans l'ordre où le tableau les montre :
 * les clients facturables d'abord, ceux qui attendent le mois ensuite, et
 * l'alphabétique à l'intérieur de chaque paquet.
 *
 * Ce classement n'est pas cosmétique : les clients au mois sont DÉCOCHÉS par
 * défaut, et les laisser dispersés dans la liste ferait une colonne de cases à
 * cocher en pointillé qu'on ne lit plus.
 */
export function grouperParClient(
  ordres: readonly OrdreAFacturer[],
  aujourdhui: string,
): readonly GroupeClient[] {
  const paquets = new Map<string, OrdreAFacturer[]>();
  for (const ordre of ordres) {
    const deja = paquets.get(ordre.client_id);
    if (deja === undefined) paquets.set(ordre.client_id, [ordre]);
    else deja.push(ordre);
  }

  const groupes: GroupeClient[] = [];
  for (const [clientId, liste] of paquets) {
    const premier = liste[0];
    if (premier === undefined) continue;
    const frequence = frequenceEffective(premier.frequence_facturation);
    groupes.push({
      clientId,
      nom: premier.client,
      frequence,
      ordres: liste,
      total: liste.reduce((somme, o) => somme + arrondi(nombre(o.prix_vente) * 100), 0),
      facturableLe:
        frequence === 'par_mois'
          ? premierDuMoisProchain(aujourdhui)
          : frequence === 'par_quinzaine'
            ? prochaineQuinzaine(aujourdhui)
            : frequence === 'par_semaine'
              ? prochainVendredi(aujourdhui)
              : null,
    });
  }

  return groupes.sort((a, b) => {
    const attenteA = a.facturableLe === null ? 0 : 1;
    const attenteB = b.facturableLe === null ? 0 : 1;
    if (attenteA !== attenteB) return attenteA - attenteB;
    return a.nom.localeCompare(b.nom, 'fr');
  });
}

/*
 * RIEN N'EST COCHÉ À L'OUVERTURE — demande du 10/09/2026 : « à l'ouverture de
 * à facturer tous les client sont cocher, je veux que ce ne soit pas le cas ».
 *
 * Il y avait ici une fonction `selectionParDefaut` qui cochait tous les ordres
 * sauf ceux des clients réglés au mois. Elle est SUPPRIMÉE, pas neutralisée :
 * une fonction qui rend toujours un ensemble vide est du code que personne ne
 * relit et que quelqu'un rebranche.
 *
 * POURQUOI C'ÉTAIT DANGEREUX. Le bouton porte le nombre de factures qui vont
 * naître. Tout coché d'office, il suffisait d'ouvrir l'écran et de cliquer pour
 * facturer des ordres qu'on n'avait pas regardés — et une facture émise ne se
 * défait plus, elle s'annule par un avoir. Cocher est maintenant un geste, et
 * un geste se fait exprès.
 *
 * C'est aussi la règle du 05/09 : « lorsque l'on arrive sur un menu aucun
 * filtre ne doit être appliqué de base ». Une sélection est un filtre.
 */

/**
 * COMBIEN DE FACTURES VONT NAÎTRE — le chiffre qu'affiche le bouton.
 *
 * C'est la fréquence du client qui décide, exactement comme dans `POST
 * /factures` : `par_ordre` en fait une par ordre, les deux autres une par
 * client. Le bouton ne dit PAS combien d'ordres sont cochés, il dit combien de
 * pièces on va devoir relire — décision du 09/09/2026.
 *
 * Cette règle est écrite deux fois, ici et dans l'API, et c'est voulu : le
 * bouton ne peut pas interroger le serveur à chaque clic. Un test
 * d'intégration tient les deux ensemble.
 */
export function combienDeFactures(
  groupes: readonly GroupeClient[],
  retenus: ReadonlySet<string>,
): number {
  let compte = 0;
  for (const groupe of groupes) {
    const coches = groupe.ordres.filter((o) => retenus.has(o.id));
    if (coches.length === 0) continue;
    compte += groupe.frequence === 'par_ordre' ? coches.length : 1;
  }
  return compte;
}

/**
 * LA LIGNE DE DÉCOMPTE au-dessus du tableau.
 *
 * Elle compte les CLIENTS et les ORDRES en attente — tous, cochés ou non —
 * puis dit combien attendent le mois. C'est un état des lieux de ce qui dort,
 * pas un écho de la sélection.
 */
export function decompteAFacturer(groupes: readonly GroupeClient[]): string {
  if (groupes.length === 0) return 'Aucun ordre en attente de facture.';
  const ordres = groupes.reduce((somme, g) => somme + g.ordres.length, 0);
  const total = groupes.reduce((somme, g) => somme + g.total, 0);
  const attente = groupes.filter((g) => g.facturableLe !== null).length;
  const clients = `${String(groupes.length)} client${groupes.length > 1 ? 's' : ''}`;
  const compteOrdres = `${String(ordres)} ordre${ordres > 1 ? 's' : ''} en facturation`;
  const morceaux = [clients, compteOrdres, `${montant(total)}\u00A0€ HT`];
  if (attente > 0) {
    morceaux.push(
      `${String(attente)} client${attente > 1 ? 's' : ''} facturable${attente > 1 ? 's' : ''} plus tard`,
    );
  }
  return `${morceaux.join(' · ')}.`;
}

/** Le trajet d'un ordre : « Ingré → Rouen », ou `null` s'il manque un bout. */
export function trajetDit(ordre: OrdreAFacturer): string | null {
  const depart = ordre.ville_depart ?? '';
  const arrivee = ordre.ville_arrivee ?? '';
  if (depart === '' && arrivee === '') return null;
  if (depart === '' || arrivee === '') return depart === '' ? arrivee : depart;
  return `${depart} → ${arrivee}`;
}

// ===========================================================================
// L'ÉCRAN « FACTURES » — le décompte et le filtre
// ===========================================================================

/** Ce que pèse un taux de TVA sur une pièce, avant remise globale. */
export interface BaseTaux {
  readonly taux: string | number;
  readonly base: string | number;
}

/** Une pièce telle que `GET /factures` la rend. */
export interface PieceListee {
  readonly id: string;
  readonly type: string;
  readonly numero: string | null;
  readonly client_nom: string;
  readonly date_piece: string;
  readonly echeance: string | null;
  readonly remise_globale: string | null;
  readonly emise_le: string | null;
  readonly envoye_le: string | null;
  readonly envoye_canal: string | null;
  /** Une base par taux présent. L'API ne rend PAS de total : voir ci-dessous. */
  readonly bases: readonly BaseTaux[];
  /**
   * La somme des règlements affectés — ajoutée le 10/09/2026 avec l'état
   * « payée ». Sans elle, une facture soldée s'affichait « en retard ».
   */
  readonly regle?: string | number | null;
  /** L'état et le reste dû chez Pennylane — lot 153. `null` si la copie ne connaît pas la pièce. */
  readonly pennylane_payee?: boolean | null;
  readonly pennylane_reste?: string | null;
  readonly facture_origine_numero?: string | null;
  /**
   * LA PIÈCE EST-ELLE FINALISÉE CHEZ PENNYLANE — 11/09/2026.
   *
   * L'écran s'en sert POUR CHOISIR SON BOUTON, et pour rien d'autre. Depuis le
   * lot 50, « Annuler la validation » est refusé sur une pièce finalisée chez
   * eux : la seule sortie est l'avoir. Montrer les deux boutons, ou le mauvais,
   * ferait croire à un geste qui n'existe pas.
   *
   * FACULTATIF : les pièces émises AVANT le 11/09 ne sont pas chez Pennylane,
   * et une réponse d'API plus ancienne ne porte pas ce champ du tout. Absent
   * se lit donc « pas chez eux », ce qui est vrai dans les deux cas.
   */
  readonly chez_pennylane?: boolean | null;
  /**
   * LA PIÈCE A-T-ELLE ÉTÉ DÉCLARÉE DÉJÀ FACTURÉE AILLEURS — 16/09/2026.
   *
   * L'écran en fait DEUX choses : une pastille, parce qu'une pièce qui n'est
   * pas passée par la validation n'a pas eu les mêmes contrôles ; et le retrait
   * du bouton « Annuler la validation », qui reculerait un compteur que cette
   * facture n'a jamais utilisé — donc un trou dans la série.
   *
   * `chez_pennylane` NE SUFFIT PAS à l'attraper : une facture rattachée n'a
   * justement pas d'identifiant chez eux, et se confondrait avec les pièces
   * émises avant le pivot du 11/09.
   *
   * FACULTATIF, comme son voisin : une réponse d'API plus ancienne ne porte pas
   * ce champ, et absent se lit « émise normalement », ce qui est vrai.
   */
  readonly emission_declaree_le?: string | null;
}

/**
 * LE PIED D'UNE PIÈCE DE LA LISTE, par le MÊME calcul que la page d'édition.
 *
 * L'API ne stocke aucun total — un total rangé à côté des lignes dérive à la
 * première correction. Elle rend les bases par taux ; on les repasse à
 * `ventilation` sous forme de lignes synthétiques, et la remise globale se
 * ventile exactement comme sur la facture ouverte.
 *
 * Une base est déjà un total de lignes arrondi : la quantité vaut donc 1 et le
 * prix unitaire porte la base entière. `totalLigne` la réarrondit sans la
 * changer.
 */
export function totauxDePiece(piece: PieceListee): Ventilation {
  return ventilation(
    piece.bases.map((b) => ({
      quantite: 1,
      prix_unitaire: b.base,
      remise: null,
      taux_tva: b.taux,
    })),
    piece.remise_globale,
  );
}

/**
 * LA LIGNE DE DÉCOMPTE de l'écran Factures.
 *
 * Elle compte les pièces, somme le HT, et signale deux choses qu'on veut voir
 * sans chercher : les brouillons qui traînent et les retards. Le nombre
 * d'exercices n'apparaît QUE s'il y en a deux — sur une liste d'un seul mois,
 * l'écrire serait du bruit.
 */
export function decompteFactures(
  pieces: readonly PieceListee[],
  aujourdhui: string,
): string {
  if (pieces.length === 0) return 'Aucune pièce sur cette période.';
  const ht = pieces.reduce((somme, p) => somme + totauxDePiece(p).htApresRemise, 0);
  const brouillons = pieces.filter((p) => p.emise_le === null).length;
  const retards = pieces.filter(
    (p) => etatFacture(p, aujourdhui) === 'en_retard',
  ).length;
  const exercices = new Set(pieces.map((p) => p.date_piece.slice(0, 4))).size;

  const morceaux = [
    `${String(pieces.length)} pièce${pieces.length > 1 ? 's' : ''}`,
    `${montant(ht)}\u00A0€ HT`,
  ];
  if (brouillons > 0) {
    morceaux.push(`${String(brouillons)} brouillon${brouillons > 1 ? 's' : ''}`);
  }
  if (retards > 0) {
    morceaux.push(`${String(retards)} en retard`);
  }
  if (exercices > 1) morceaux.push(`${String(exercices)} exercices`);
  return `${morceaux.join(' · ')}.`;
}

/** Le libellé d'une pièce dans la colonne « Pièce ». */
export function pieceDite(piece: PieceListee): { titre: string; sous: string } {
  const avoir = piece.type === 'avoir';
  const origine = piece.facture_origine_numero ?? null;
  return {
    titre: piece.numero ?? '—',
    sous: avoir ? (origine === null ? 'Avoir' : `Avoir · sur ${origine}`) : 'Facture',
  };
}
